import { useEffect, useState, useCallback } from 'react'
import { useSnackbar } from 'notistack'

type EnqueueSnackbar = ReturnType<typeof useSnackbar>['enqueueSnackbar']

interface ServiceWorkerState {
    isSupported: boolean
    isRegistered: boolean
    isInstalling: boolean
    isWaiting: boolean
    registration: ServiceWorkerRegistration | null
    cacheVersion: string | null
    cacheName: string | null
    lastCacheRefresh: Date | null
}

const isDevelopment = () => {
    return (
        import.meta.env.DEV ||
        import.meta.env.MODE === 'development' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.startsWith('192.168.') ||
        window.location.hostname.startsWith('10.') ||
        window.location.port !== ''
    )
}

// ───────────────────────────────────────────────────────────────────────────
// Shared, process-wide state.
//
// The hook is consumed by several components at once (ServiceWorkerInitializer,
// OfflineStatusIndicator, HomePage, LoginPage, …). Previously each consumer
// registered its own `navigator.serviceWorker.register` call AND its own
// `message` listener, so a single broadcast from the SW (e.g. a forced refresh)
// was handled N times — N navigations stacking on top of each other. We now
// register and listen exactly once and fan state out to every subscriber.
// ───────────────────────────────────────────────────────────────────────────
const createInitialState = (): ServiceWorkerState => ({
    isSupported: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    isRegistered: false,
    isInstalling: false,
    isWaiting: false,
    registration: null,
    cacheVersion: null,
    cacheName: null,
    lastCacheRefresh: null
})

let sharedState = createInitialState()
const subscribers = new Set<(state: ServiceWorkerState) => void>()

const setSharedState = (updater: (prev: ServiceWorkerState) => ServiceWorkerState) => {
    sharedState = updater(sharedState)
    subscribers.forEach(notify => notify(sharedState))
}

let registrationSingleton: ServiceWorkerRegistration | null = null
let initStarted = false
let updatePromptShown = false

// ───────────────────────────────────────────────────────────────────────────
// Reload-loop circuit breaker.
//
// The SW deletes all caches and broadcasts a "new version" message whenever its
// (in-memory, reset-on-restart) version differs from version.json. If that
// comparison keeps mismatching, the page would reload forever — fast enough to
// look frozen, with nothing in the console because it's all log, not error.
// We track recent reloads in sessionStorage and refuse to auto-reload once we
// cross the threshold, surfacing the manual "Update" prompt instead.
// ───────────────────────────────────────────────────────────────────────────
const RELOAD_TIMESTAMPS_KEY = 'sw:reload-timestamps'
const RELOAD_MAX = 2
const RELOAD_WINDOW_MS = 10_000

const isReloadStorm = (): boolean => {
    try {
        const now = Date.now()
        const stored: number[] = JSON.parse(sessionStorage.getItem(RELOAD_TIMESTAMPS_KEY) || '[]')
        const recent = stored.filter(timestamp => now - timestamp < RELOAD_WINDOW_MS)
        recent.push(now)
        sessionStorage.setItem(RELOAD_TIMESTAMPS_KEY, JSON.stringify(recent))
        return recent.length > RELOAD_MAX
    } catch {
        // If sessionStorage is unavailable, fail open (don't block) but also
        // don't pretend we're looping.
        return false
    }
}

// Show a single, persistent "click to update" prompt. Both the version-detected
// and forced-refresh SW messages route through here so the user — not a timer —
// decides when to reload. Idempotent: only one prompt is shown at a time.
const promptForUpdate = (enqueueSnackbar: EnqueueSnackbar) => {
    if (updatePromptShown) return
    updatePromptShown = true

    enqueueSnackbar('New version available! Click to update.', {
        variant: 'info',
        persist: true,
        action: () => {
            const button = document.createElement('button')
            button.textContent = 'Update'
            button.style.background = 'transparent'
            button.style.border = 'none'
            button.style.color = 'white'
            button.style.textDecoration = 'underline'
            button.style.cursor = 'pointer'
            button.onclick = () => {
                if (registrationSingleton?.waiting) {
                    registrationSingleton.waiting.postMessage({ type: 'SKIP_WAITING' })
                }
                const url = new URL(window.location.href)
                url.searchParams.set('cache-bust', Date.now().toString())
                window.location.href = url.toString()
            }
            return button
        }
    })
}

// Reload only when we're confident we're not in a loop. Used for the dev-mode
// auto-reload path; production always prompts.
const safeAutoReload = (enqueueSnackbar: EnqueueSnackbar) => {
    if (isReloadStorm()) {
        console.warn('[SW] Reload loop detected — suppressing automatic reload and prompting instead')
        promptForUpdate(enqueueSnackbar)
        return
    }
    window.location.reload()
}

// Helper function to get cache information from service worker
const getCacheInfo = async (): Promise<{ version: string; cacheName: string } | null> => {
    if (!('serviceWorker' in navigator)) return null

    return new Promise((resolve) => {
        const messageChannel = new MessageChannel()

        messageChannel.port1.onmessage = (event) => {
            if (event.data && event.data.type === 'CACHE_INFO') {
                resolve({
                    version: event.data.version,
                    cacheName: event.data.cacheName
                })
            } else {
                resolve(null)
            }
        }

        if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage(
                { type: 'GET_CACHE_INFO' },
                [messageChannel.port2]
            )
        } else {
            resolve(null)
        }

        // Timeout after 2 seconds
        setTimeout(() => resolve(null), 2000)
    })
}

// Helper function to force cache refresh
const forceCacheRefresh = async (): Promise<boolean> => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        return false
    }

    // In development mode, don't try to refresh cache since it's disabled
    if (isDevelopment()) {
        console.log('Cache refresh skipped: caching is disabled in development mode')
        return false
    }

    return new Promise((resolve) => {
        navigator.serviceWorker.controller!.postMessage({ type: 'FORCE_CACHE_REFRESH' })

        // Listen for cache refresh confirmation
        const messageHandler = (event: MessageEvent) => {
            if (event.data && event.data.type === 'CACHE_REFRESHED') {
                navigator.serviceWorker.removeEventListener('message', messageHandler)
                resolve(true)
            }
        }

        navigator.serviceWorker.addEventListener('message', messageHandler)

        // Timeout after 10 seconds
        setTimeout(() => {
            navigator.serviceWorker.removeEventListener('message', messageHandler)
            resolve(false)
        }, 10000)
    })
}

// One-time registration + message wiring for the whole app.
const initServiceWorker = (enqueueSnackbar: EnqueueSnackbar) => {
    if (initStarted) return
    if (!('serviceWorker' in navigator)) {
        console.log('Service Worker not supported')
        return
    }
    initStarted = true

    const register = async () => {
        try {
            const registration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/'
            })
            registrationSingleton = registration

            setSharedState(prev => ({
                ...prev,
                isRegistered: true,
                registration
            }))

            // Handle service worker updates
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing
                if (!newWorker) return

                setSharedState(prev => ({ ...prev, isInstalling: true }))

                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state !== 'installed') return

                    setSharedState(prev => ({ ...prev, isInstalling: false }))

                    if (navigator.serviceWorker.controller) {
                        // New version available
                        setSharedState(prev => ({ ...prev, isWaiting: true }))

                        if (isDevelopment()) {
                            // In development, reload automatically since caching is disabled
                            console.log('Service Worker updated, reloading...')
                            newWorker.postMessage({ type: 'SKIP_WAITING' })
                            safeAutoReload(enqueueSnackbar)
                        } else {
                            // In production, let the user choose when to reload
                            promptForUpdate(enqueueSnackbar)
                        }
                    } else {
                        // First time installation
                        enqueueSnackbar(
                            isDevelopment()
                                ? 'Offline mode ready for testing! (caching disabled in dev)'
                                : 'App is ready for offline use!',
                            { variant: 'success' }
                        )
                    }
                })
            })

            // Listen for service worker messages (registered exactly once)
            navigator.serviceWorker.addEventListener('message', (event) => {
                const data = event.data
                if (!data) return

                if (data.type === 'SW_UPDATE') {
                    if (isDevelopment()) {
                        safeAutoReload(enqueueSnackbar)
                    } else {
                        promptForUpdate(enqueueSnackbar)
                    }
                    return
                }

                if (data.type === 'CACHE_REFRESHED') {
                    const refreshedVersion = String(data.version).trim()
                    console.log('Cache refreshed, version:', refreshedVersion)

                    // Only update if this is actually a newer version than what we
                    // already have, to avoid accidentally downgrading the version.
                    setSharedState(prev => {
                        if (!prev.cacheVersion || refreshedVersion !== prev.cacheVersion) {
                            return {
                                ...prev,
                                cacheVersion: refreshedVersion,
                                lastCacheRefresh: new Date()
                            }
                        }
                        return prev
                    })
                    return
                }

                if (data.type === 'VERSION_UPDATE_AVAILABLE') {
                    const serverVersion = String(data.serverVersion).trim()
                    const currentVersion = String(data.currentVersion).trim()

                    console.log('New version available:', serverVersion, 'current:', currentVersion)

                    if (isDevelopment()) {
                        console.log('Development mode: New version detected but caching is disabled')
                        return
                    }

                    // Safety check: if versions are actually the same, do nothing.
                    if (serverVersion === currentVersion) {
                        console.log('Version mismatch resolved - no update needed')
                        setSharedState(prev => ({
                            ...prev,
                            cacheVersion: serverVersion,
                            cacheName: `telemed-offline-${serverVersion}`
                        }))
                        return
                    }

                    setSharedState(prev => ({ ...prev, cacheVersion: serverVersion }))
                    promptForUpdate(enqueueSnackbar)
                    return
                }

                if (data.type === 'NEW_VERSION_AVAILABLE_FORCE_REFRESH') {
                    // This message used to trigger an automatic navigation. That is
                    // exactly what caused the production freeze: when the SW's
                    // in-memory version reset across worker restarts, the comparison
                    // kept mismatching and the page reloaded continuously. We now
                    // surface the manual prompt instead — never auto-navigate.
                    const version = String(data.version).trim()
                    console.log('New version available (force-refresh request):', version)

                    setSharedState(prev => ({
                        ...prev,
                        cacheVersion: version,
                        cacheName: `telemed-offline-${version}`
                    }))
                    promptForUpdate(enqueueSnackbar)
                    return
                }

                if (data.type === 'CACHE_CLEANUP_COMPLETE') {
                    const cleanupVersion = String(data.version).trim()
                    console.log('Cache cleanup complete, new version active:', cleanupVersion)
                    setSharedState(prev => ({
                        ...prev,
                        cacheVersion: cleanupVersion,
                        lastCacheRefresh: new Date()
                    }))
                    return
                }
            })

            // Get cache information
            getCacheInfo().then(cacheInfo => {
                if (cacheInfo) {
                    setSharedState(prev => ({
                        ...prev,
                        cacheVersion: cacheInfo.version,
                        cacheName: cacheInfo.cacheName
                    }))

                    if (isDevelopment() && cacheInfo.version === 'development-mode') {
                        console.log('Service Worker in development mode: caching is disabled')
                    }
                }
            })

            console.log(
                `${isDevelopment() ? 'Service Worker registered for development testing' : 'Service Worker registered successfully'}:`,
                registration.scope
            )
        } catch (error) {
            // Allow a later mount to retry registration.
            initStarted = false
            console.error('Service Worker registration failed:', error)
            enqueueSnackbar(
                isDevelopment()
                    ? 'Failed to enable offline testing mode'
                    : 'Failed to enable offline support',
                { variant: 'error' }
            )
        }
    }

    register()
}

export const useServiceWorker = () => {
    const { enqueueSnackbar } = useSnackbar()
    const [swState, setSwState] = useState<ServiceWorkerState>(sharedState)

    useEffect(() => {
        if (!sharedState.isSupported) {
            console.log('Service Worker not supported')
            return
        }

        subscribers.add(setSwState)
        // Sync immediately in case state advanced before this component mounted.
        setSwState(sharedState)
        initServiceWorker(enqueueSnackbar)

        return () => {
            subscribers.delete(setSwState)
        }
    }, [enqueueSnackbar])

    const updateServiceWorker = useCallback(() => {
        registrationSingleton?.waiting?.postMessage({ type: 'SKIP_WAITING' })
    }, [])

    const checkForUpdates = useCallback(async () => {
        if (registrationSingleton) {
            console.log('Manual update check triggered')
            await registrationSingleton.update()
        }
    }, [])

    const forceVersionCheck = useCallback(async () => {
        // Trigger SW to check for updates via message passing
        if (!navigator.serviceWorker.controller) {
            console.warn('No active service worker to check version')
            return false
        }

        return new Promise<boolean>((resolve) => {
            const messageChannel = new MessageChannel()

            messageChannel.port1.onmessage = (event) => {
                if (event.data && event.data.type === 'VERSION_CHECK_COMPLETE') {
                    const updateFound = event.data.updateFound
                    if (isDevelopment()) {
                        console.log('Version check complete, update found:', updateFound)
                    }
                    resolve(updateFound)
                }
            }

            // Ensure controller is not null before posting message
            if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage(
                    { type: 'FORCE_VERSION_CHECK' },
                    [messageChannel.port2]
                )
            }

            // Timeout after 10 seconds
            setTimeout(() => {
                console.warn('Version check timeout')
                resolve(false)
            }, 10000)
        })
    }, [])

    const refreshCache = useCallback(async () => {
        // In development mode, provide appropriate feedback
        if (isDevelopment()) {
            console.log('Cache refresh: caching is disabled in development mode')
            enqueueSnackbar('Cache refresh skipped: caching is disabled in development', { variant: 'info' })
            return false
        }

        const success = await forceCacheRefresh()
        if (success) {
            enqueueSnackbar('Cache refreshed successfully!', { variant: 'success' })
        } else {
            enqueueSnackbar('Failed to refresh cache', { variant: 'error' })
        }
        return success
    }, [enqueueSnackbar])

    const clearAllCaches = useCallback(async () => {
        if ('caches' in window) {
            const cacheNames = await caches.keys()

            // In development mode, give feedback that caching is disabled
            if (isDevelopment()) {
                console.log('Clear caches: caching is disabled in development mode')
                if (cacheNames.length === 0) {
                    enqueueSnackbar('No caches to clear in development mode', { variant: 'info' })
                } else {
                    // Still clear any existing caches if they exist
                    await Promise.all(cacheNames.map(name => caches.delete(name)))
                    enqueueSnackbar('All caches cleared!', { variant: 'success' })
                }
            } else {
                await Promise.all(cacheNames.map(name => caches.delete(name)))
                enqueueSnackbar('All caches cleared!', { variant: 'success' })
            }

            setSharedState(prev => ({ ...prev, lastCacheRefresh: new Date() }))
            return true
        }
        return false
    }, [enqueueSnackbar])

    return {
        ...swState,
        updateServiceWorker,
        checkForUpdates,
        forceVersionCheck,
        refreshCache,
        clearAllCaches
    }
}
